import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface FilterOption {
  value: string;
  label: string;
}

interface FilterDropdownProps {
  icon?: LucideIcon;
  options: FilterOption[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

export function FilterDropdown({ icon: Icon, options, value, onChange, className }: FilterDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const selected = options.find(o => o.value === value);
  const isFiltered = value !== '' && value !== options[0]?.value;

  return (
    <div className={`relative${className ? ` ${className}` : ''}`} ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
          isFiltered
            ? 'border-accent/30 bg-accent/10 text-accent'
            : 'border-border-default bg-bg-tertiary text-text-secondary hover:text-text-primary'
        }`}
      >
        {Icon && <Icon className="h-3 w-3" />}
        {selected?.label || options[0]?.label}
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute start-0 top-full z-20 mt-1 min-w-[8rem] rounded-lg border border-border-default bg-bg-secondary shadow-xl overflow-hidden">
          {options.map(opt => (
            <button
              key={opt.value}
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className={`flex w-full items-center gap-2 px-3 py-2 text-xs transition-colors ${
                value === opt.value ? 'bg-accent/10 text-accent font-semibold' : 'text-text-secondary hover:bg-bg-hover'
              }`}
            >
              {value === opt.value && <Check className="h-3 w-3 shrink-0" />}
              <span className={value !== opt.value ? 'ps-5' : ''}>{opt.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
